<template>
    <ModalBase
        :name="props.name"
        title="Descargar resumen de ventas"
        subtitle="El informe se genera en segundo plano y se avisa por notificación cuando está listo."
        :width="720"
        @opened="sincronizarConFiltros"
        @close="$emit('close')"
    >
        <div class="tw:flex tw:flex-col tw:gap-4">
            <div class="tw:grid tw:grid-cols-1 tw:gap-4 tw:sm:grid-cols-2">
                <div class="tw:flex tw:flex-col tw:gap-1">
                    <label :class="labelClass" for="descargar-resumen-del">Del</label>
                    <BaseDateInput id="descargar-resumen-del" :value="fecha_inicio" data-cy="descargar-resumen-del" @input="fecha_inicio = $event" />
                </div>
                <div class="tw:flex tw:flex-col tw:gap-1">
                    <label :class="labelClass" for="descargar-resumen-al">Al</label>
                    <BaseDateInput id="descargar-resumen-al" :value="fecha_fin" data-cy="descargar-resumen-al" @input="fecha_fin = $event" />
                </div>
            </div>

            <div class="tw:flex tw:flex-col tw:gap-1">
                <label :class="labelClass" for="descargar-resumen-formato">Formato</label>
                <BaseSelect
                    id="descargar-resumen-formato"
                    :value="formato"
                    :options="formatos"
                    data-cy="descargar-resumen-formato"
                    @change="formato = $event"
                />
            </div>

            <div class="tw:grid tw:grid-cols-1 tw:gap-4 tw:sm:grid-cols-3">
                <div class="tw:flex tw:flex-col tw:gap-1">
                    <label :class="labelClass" for="descargar-resumen-teatro">Teatro</label>
                    <BaseMultiselect
                        id="descargar-resumen-teatro"
                        name="descargar-resumen-teatro"
                        :value="teatroId"
                        :options="props.teatros"
                        label-key="nombre"
                        placeholder="Todos los recintos"
                        data-cy="descargar-resumen-teatro"
                        @input="teatroId = $event || ''"
                    />
                </div>

                <div class="tw:flex tw:flex-col tw:gap-1">
                    <label :class="labelClass" for="descargar-resumen-taquillero">Taquillero</label>
                    <BaseMultiselect
                        id="descargar-resumen-taquillero"
                        name="descargar-resumen-taquillero"
                        :value="taquilleroId"
                        :options="props.taquilleros"
                        label-key="nombre"
                        placeholder="Todos los agentes"
                        data-cy="descargar-resumen-taquillero"
                        @input="taquilleroId = $event || ''"
                    />
                </div>

                <div class="tw:flex tw:flex-col tw:gap-1">
                    <label :class="labelClass" for="descargar-resumen-compania">Compañía</label>
                    <BaseMultiselect
                        id="descargar-resumen-compania"
                        name="descargar-resumen-compania"
                        :value="companiaId"
                        :options="props.companias"
                        label-key="nombre"
                        placeholder="Todas las compañías"
                        data-cy="descargar-resumen-compania"
                        @input="companiaId = $event || ''"
                    />
                </div>
            </div>
        </div>

        <template #footer>
            <div class="tw:flex tw:items-center tw:justify-end tw:gap-3">
                <Button variant="ghost" @click="$emit('close')">Cerrar</Button>
                <Button
                    icon="fas fa-file-download"
                    :disabled="enviando"
                    data-cy="descargar-resumen-enviar"
                    @click="descargar"
                >
                    Descargar
                </Button>
            </div>
        </template>
    </ModalBase>
</template>

<script setup>
    import { ref, getCurrentInstance } from 'vue';
    import store from '@/store';
    import { config } from '@/config.js';

    import ModalBase from '@/modules/admin/ui/ModalBase.vue';
    import Button from '@/modules/admin/ui/Button.vue';
    import BaseDateInput from '@/modules/admin/ui/BaseDateInput.vue';
    import BaseSelect from '@/modules/admin/ui/BaseSelect.vue';
    import BaseMultiselect from '@/modules/admin/ui/BaseMultiselect.vue';

    const props = defineProps({
        name: {
            type: String,
            required: true
        },
        /** Foto de los filtros vigentes en la pestaña. */
        filtros: {
            type: Object,
            default: () => ({})
        },
        teatros: {
            type: Array,
            default: () => []
        },
        taquilleros: {
            type: Array,
            default: () => []
        },
        companias: {
            type: Array,
            default: () => []
        }
    });

    const emit = defineEmits(['close']);

    const instance = getCurrentInstance();

    const labelClass = 'tw:block tw:text-xs tw:font-semibold tw:text-slate-500 tw:uppercase tw:tracking-wide';

    const formatos = [
        { value: 'xls', label: 'Excel (.xls)' },
        { value: 'pdf', label: 'PDF' }
    ];

    const fecha_inicio = ref('');
    const fecha_fin = ref('');
    const formato = ref('xls');
    const teatroId = ref('');
    const taquilleroId = ref('');
    const companiaId = ref('');
    /**
     * Petición en vuelo. Deshabilita el botón SIN spinner: la descarga no se
     * espera aquí (el fichero llega por notificación), pero un segundo clic
     * mientras el `dispatch` está pendiente pediría otro informe y generaría
     * notificaciones duplicadas.
     */
    const enviando = ref(false);


    /**
     * El modal se rellena con los filtros vigentes CADA VEZ que se abre (no en
     * el `created` como el legacy): si el usuario cambió el rango y vuelve a
     * abrir, el informe sale con lo que está viendo, no con lo de la primera
     * apertura.
     */
    function sincronizarConFiltros() {
        fecha_inicio.value = props.filtros.del || props.filtros.fecha_inicio || '';
        fecha_fin.value = props.filtros.al || props.filtros.fecha_fin || '';
        teatroId.value = props.filtros.teatro_id !== undefined ? props.filtros.teatro_id : '';
        taquilleroId.value = props.filtros.taquillero_id !== undefined ? props.filtros.taquillero_id : '';
        companiaId.value = props.filtros.teatro_company_id !== undefined ? props.filtros.teatro_company_id : '';
    }

    /**
     * El informe se genera en segundo plano: la descarga llega por la campana
     * de notificaciones. Al aceptar el modal no hay nada que esperar, así que
     * se cierra en cuanto la petición se acepta. Dejar el botón con spinner
     * era una espera falsa: la respuesta llega al instante y el fichero, más
     * tarde.
     */
    function descargar() {
        // Guard además del `disabled`: un doble clic en el mismo tick puede
        // colarse antes del re-render que deshabilita el botón.
        if (enviando.value) return;

        enviando.value = true;

        return store.dispatch(config.getVuexAlias('back.events.resumen-ventas.descargar'), {
            filters: {
                fecha_inicio: fecha_inicio.value,
                fecha_fin: fecha_fin.value,
                format: formato.value,
                search_teatro_id: teatroId.value,
                taquillero_id: taquilleroId.value,
                teatro_company_id: companiaId.value,
                pasado: !!props.filtros.pasado,
                actuales: !!props.filtros.actual
            }
        }).then(() => {
            // El informe se genera en segundo plano: la descarga llega por la
            // campana de notificaciones. Se avisa al usuario y se cierra el
            // modal; no hay nada que esperar aquí.
            //
            // El bloqueo se libera AQUÍ, no al abrir el modal: si el usuario lo
            // cerró con la petición en vuelo, al reabrirlo el botón sigue
            // deshabilitado hasta que esta termine.
            enviando.value = false;
            instance.proxy.$toast.success('Se está generando el informe. Recibirás una notificación cuando esté listo.');
            emit('close');
        }).catch(error => {
            // El modal NO se cierra en el error: el usuario tiene que poder
            // reintentar sin volver a configurar el informe.
            enviando.value = false;
            instance.proxy.$toast.error('No se ha podido solicitar el informe. Intentá de nuevo.');
            if (process.env.NODE_ENV !== 'production') console.error(error);
        });
    }
</script>
